/**
 * Centralised App Router path builders — the single source of truth for every
 * in-app page URL. A route move is one edit here instead of a grep-and-replace
 * across every `href` / `router.push` / `redirect`. Callers never hand-assemble
 * a path or a query string.
 *
 * Conventions:
 *   - Static routes are string constants (`routes.about`).
 *   - Dynamic routes are builder functions (`routes.business(id)`), including
 *     the optional `[tab]` segment where the app tree has one.
 *   - Grouped areas (profinder, my-businesses) are nested objects.
 *
 * Scope: **page routes only**. `/api/*`, `/internal/*`, and `/.well-known/*`
 * are endpoints, not navigation targets, and are intentionally excluded — as
 * are backend Go API paths, which are a separate concern entirely.
 *
 * The ProFinder results builder is the inverse of the URL resolver's profinder
 * parse: the path + param names live in one place instead of being encoded by
 * both the resolver and every card that links back.
 */

import { profinderUrl } from './profinder-site'
import { shopUrl } from './shop-site'

/** The standalone ProFinder `/results` query contract (all optional). */
export type ProfinderResultsParams = {
  q?: string
  zip?: string
  fid?: string
  cid?: string
  hids?: string[]
  sort?: 'featured' | 'name' | 'rating'
  page?: number
  limit?: number
}

function profinderResults(params: ProfinderResultsParams = {}): string {
  const sp = new URLSearchParams()
  if (params.q) sp.set('q', params.q)
  if (params.zip) sp.set('zip', params.zip)
  if (params.fid) sp.set('fid', params.fid)
  if (params.cid) sp.set('cid', params.cid)
  if (params.hids?.length) sp.set('hids', params.hids.join(','))
  if (params.sort && params.sort !== 'featured') sp.set('sort', params.sort)
  if (params.page && params.page > 1) sp.set('page', String(params.page))
  if (params.limit && params.limit !== 12) sp.set('limit', String(params.limit))
  const qs = sp.toString()
  // eslint-disable-next-line routes/route-exists -- This route belongs to the standalone ProFinder site.
  return profinderUrl(qs ? `/results?${qs}` : '/results')
}

export const routes = {
  // ── Top-level / marketing ────────────────────────────────────────────────
  root: '/',
  recoverAccount: '/recover-account',
  home: '/home',
  about: '/about',
  contact: '/contact',
  blog: '/blog',
  forBusinesses: '/for-businesses',
  restaurants: '/restaurants',
  shop: '/shop',
  whatsNew: '/whats-new',
  /** Public download page for HubHive Loop, the messaging app. */
  loop: '/loop',

  // ── Auth / account ───────────────────────────────────────────────────────
  /** `callbackUrl` is the post-auth return path; it is URL-encoded for you. */
  login: (callbackUrl?: string) =>
    callbackUrl ? `/login?callbackUrl=${encodeURIComponent(callbackUrl)}` : '/login',
  /**
   * The Connect page (ADR-0150): where `/oauth/authorize` sends a Sign in
   * with HubHive request to be decided with the site session. `request` is
   * the pending-request handle it arrived with. `switched` marks the way
   * back from a sign-in (the login adds it to every return here, see
   * markChosenBySignIn): the person chose by signing in, so the page decides
   * for the new session without drawing the chooser.
   */
  connect: (request: string, options?: { switched?: boolean; consent?: boolean }) =>
    `/connect?request=${encodeURIComponent(request)}${options?.switched ? '&switched=1' : ''}${options?.consent ? '&consent=1' : ''}`,
  signup: '/signup',
  settings: '/settings',
  deleteAccount: '/delete-account',
  unsubscribe: '/unsubscribe',

  // ── Legal ──────────────────────────────────────────────────────────────────
  termsOfService: '/terms-of-service',
  privacyPolicy: '/privacy-policy',

  // ── Social / discovery ───────────────────────────────────────────────────
  alerts: '/alerts',
  /** The clips feed (T4.7) — vertical, one clip at a time. */
  clips: '/clips',
  messages: '/messages',
  /** One open Conversation in the web chat shell (#692). */
  conversation: (conversationId: string) => `/messages/${conversationId}`,
  people: '/people',
  search: '/search',
  searchResults: '/search-results',
  exploreHives: '/explore-hives',

  // ── Collections ──────────────────────────────────────────────────────────
  hives: '/hives',
  /** The hives list with its search pre-filled. */
  hivesSearch: (q: string) => `/hives?${new URLSearchParams({ q: q.trim() })}`,
  events: '/events',
  /** `q` pre-fills the directory search; `create` opens the "list a business" flow on arrival. */
  businesses: (opts?: { create?: boolean; q?: string }) => {
    const params = new URLSearchParams()
    if (opts?.q?.trim()) params.set('q', opts.q.trim())
    if (opts?.create) params.set('create', '1')
    return params.size ? `/businesses?${params}` : '/businesses'
  },
  businessesDirectory: '/businesses-directory',
  // eslint-disable-next-line routes/route-exists -- The services directory is hosted by ProFinder.
  servicesDirectory: profinderUrl('/services'),
  /** Singular `/product` marketing page — NOT an entity (that's `products(id)`). */
  productMarketing: '/product',

  // ── Entities (detail; optional `[tab]` where the app tree has one) ─────────
  business: (id: string, tab?: string) => (tab ? `/business/${id}/${tab}` : `/business/${id}`),
  externalBusiness: (id: string) => `/businesses/external/${id}`,
  event: (id: string, tab?: string) => (tab ? `/event/${id}/${tab}` : `/event/${id}`),
  hive: (id: string, tab?: string) => (tab ? `/hive/${id}/${tab}` : `/hive/${id}`),
  hiveManage: (id: string) => `/hive/${id}/manage`,
  post: (id: string) => `/post/${id}`,
  product: (id: string) => `/products/${id}`,
  profile: (id: string) => `/profile/${id}`,

  // ── My businesses ──────────────────────────────────────────────────────────
  myBusinesses: {
    index: () => '/my-businesses',
    quotes: (id: string) => `/my-businesses/${id}/quotes`,
  },

  // ── ProFinder ──────────────────────────────────────────────────────────────
  /* eslint-disable routes/route-exists -- These are external routes hosted by the standalone ProFinder project. */
  profinder: {
    /** `focus: 'search'` deep-links to the search field (`?focus=search`). */
    index: (opts?: {
      focus?: 'search'
      q?: string
      zip?: string
      fid?: string
      autoSearch?: boolean
    }) => {
      const params = new URLSearchParams()
      if (opts?.focus) params.set('focus', opts.focus)
      if (opts?.q) params.set('q', opts.q.trim())
      if (opts?.zip) params.set('zip', opts.zip)
      if (opts?.fid) params.set('fid', opts.fid)
      if (opts?.autoSearch) params.set('autoSearch', '1')
      return profinderUrl(params.size ? `/?${params}` : '/')
    },
    results: profinderResults,
    local: (stateCode: string, citySlug: string, serviceSlug: string) =>
      profinderUrl(
        `/${encodeURIComponent(stateCode.toLowerCase())}/${encodeURIComponent(citySlug)}/${encodeURIComponent(serviceSlug)}`
      ),
    search: () => profinderResults(),
    service: () => profinderResults(),
    claim: (id: string) => profinderUrl(`/pro/${encodeURIComponent(id)}/claim`),
    manageServices: (id: string) => profinderUrl(`/pro/${encodeURIComponent(id)}/services`),
    pro: (id: string, returnTo?: string) => {
      const path = profinderUrl(`/pro/${encodeURIComponent(id)}`)
      return returnTo ? `${path}?${new URLSearchParams({ returnTo })}` : path
    },
    request: (id: string, serviceId?: string, returnTo?: string) => {
      const path = profinderUrl(`/pro/${encodeURIComponent(id)}/request`)
      const params = new URLSearchParams()
      if (serviceId) params.set('service', serviceId)
      if (returnTo) params.set('returnTo', returnTo)
      return params.size ? `${path}?${params}` : path
    },
  },
  /* eslint-enable routes/route-exists */

  // ── HubHive Shop (standalone site) ─────────────────────────────────────────
  /* eslint-disable routes/route-exists -- These are external routes hosted by the standalone HubHive Shop. */
  shopSite: {
    index: () => shopUrl('/'),
    browse: () => shopUrl('/browse'),
    clips: () => shopUrl('/clips'),
    search: (q: string) => shopUrl(`/search?${new URLSearchParams({ q: q.trim() })}`),
    product: (id: string) => shopUrl(`/p/${encodeURIComponent(id)}`),
    store: (slug: string) => shopUrl(`/b/${encodeURIComponent(slug)}`),
  },
  /* eslint-enable routes/route-exists */

  // ── Entry / share links ──────────────────────────────────────────────────
  invite: (token: string) => `/invite/${token}`,
  joinHive: (id: string) => `/join/hive/${id}`,
  shortLink: (shortCode: string) => `/s/${shortCode}`,
} as const
